import { TemplateResult, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js'
import globalStyles from './styles/outline-homepage-hero.global.pcss?inline';
import encapsulatedStyles from './styles/outline-homepage-hero.base.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import '../outline-accent-bar/outline-accent-bar';
import '../outline-container/outline-container';

/**
 * The OutlineHomepageHero component
 * @element outline-homepage-hero
 */
@customElement('outline-homepage-hero')
export class OutlineHomepageHero extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  private _ctaObserver?: ResizeObserver;
  private _boundSetImageSizingVars!: () => void;

  connectedCallback() {
    super.connectedCallback();
    // Set 'this' to the resize listener
    this._boundSetImageSizingVars = this.setImageSizingVars.bind(this);
    window.addEventListener('resize', this._boundSetImageSizingVars);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._boundSetImageSizingVars);
    this._ctaObserver?.disconnect();
    super.disconnectedCallback();
  }

  firstUpdated() {
    // Observe CTAs for size changes
    const cta = this.shadowRoot?.querySelector('.homepage-hero--cta-list') as HTMLElement | null;
    if (cta) {
      this._ctaObserver = new ResizeObserver(() => this.setCtaSpacer());
      this._ctaObserver.observe(cta);
    }

    // Run when the image actually loads
    const img = this.querySelector('img');
    if (img && !img.complete) {
      img.addEventListener('load', () => {
          this.setImageSizingVars();
          this.setCtaSpacer();
        }, { once: true }
      );
    }
    // Initial setup after render
    setTimeout(() => {
      this.setImageSizingVars();
      this.setCtaSpacer();
    }, 0);
  }

  render(): TemplateResult {
    return html`
      <div class="homepage-hero">
        <outline-container x-padding="space-0" y-padding="space-0" content-max-width="plus">
          <div class="homepage-hero--main-content">
            <div class="homepage-hero--top-content">
              <div class="homepage-hero--text">
                <slot name="heading"></slot>
                <slot name="description"></slot>
              </div>
              <div class="homepage-hero--image">
                <slot name="image" @slotchange=${this.setImageSizingVars}></slot>
              </div>
            </div>
            <div class="homepage-hero--cta-list">
              <slot name="ctas" @slotchange=${this.setCtaSpacer}></slot>
            </div>
          </div>
        </outline-container>
        <div>
          <outline-accent-bar context="homepage-hero"></outline-accent-bar>
        </div>
      </div>
    `;
  }

  /**
   * Calculates image sizing variables and triggers CTA spacer recalculation.
   */
  setImageSizingVars() {
    const imageEl = this.querySelector('img');

    if (imageEl) {
      this.setImageWidthVar();
      this.setNegativeMarginVar(imageEl);
      this.setCtaSpacer();
    }
  }

  /**
   * Sets the image width vars to be 50% or 40% of window size.
   */
  setImageWidthVar() {
    const windowWidth = window.innerWidth;
    const imageFullWidth = (windowWidth * .5);
    const imageReducedWidth = (windowWidth * .4);
    this.style.setProperty('--image-full-width', `${imageFullWidth}px`);
    this.style.setProperty('--image-reduced-width', `${imageReducedWidth}px`);
  }

  /**
   * Takes the window and image size to determine the required negative margin to
   * correctly position the image on the right edge of the screen.
   */
  setNegativeMarginVar(image: HTMLImageElement) {
    this.style.setProperty('--negative-margin', `0px`);
    const windowWidth = window.innerWidth;
    const rightPosition = (windowWidth - image.getBoundingClientRect().right) * -1;
    this.style.setProperty('--negative-margin', `${rightPosition}px`);
  }

  /**
   * Sets the height for the pseudo-element below the text content to equal the height of the cta-nav
   * to prevent it from sliding up and covering the text at smaller screens.
   */
  setCtaSpacer() {
    const ctaList = this.shadowRoot?.querySelector('.homepage-hero--cta-list') as HTMLElement | null;
    if (!ctaList) return;

    const rect = ctaList.getBoundingClientRect();
    const cs = getComputedStyle(ctaList);
    const marginTop = parseFloat(cs.marginTop) || 0;
    const marginBottom = parseFloat(cs.marginBottom) || 0;
    const buffer = 1;

    const total = Math.ceil(rect.height + marginTop + marginBottom + buffer);
    this.style.setProperty('--cta-height', `${total}px`);
  }

}
